import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { resolveImageUrl, formatPostDate } from '../lib/blogApi';
import { Calendar, User, ArrowLeft, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const { getCartItemCount } = useCart();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API}/blog/posts/${slug}`)
      .then((res) => {
        if (active) setPost(res.data);
      })
      .catch(() => {
        if (active) setNotFound(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      <div className="pt-28 pb-20">
        <div className="container mx-auto px-6 max-w-3xl">
          <Link to="/blog" className="inline-flex items-center text-[#DB5A14] hover:text-[#F39C49] font-bold mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Community
          </Link>

          {loading && (
            <div className="flex justify-center py-24" data-testid="blog-post-loading">
              <Loader2 className="w-10 h-10 text-[#004D51] animate-spin" />
            </div>
          )}

          {!loading && notFound && (
            <div className="text-center py-24">
              <h1 className="text-4xl font-black text-[#004D51] mb-4">Post Not Found</h1>
              <p className="text-[#545759] mb-8">This article may have been moved or unpublished.</p>
              <Link to="/blog">
                <Button className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-bold">View All Posts</Button>
              </Link>
            </div>
          )}

          {!loading && post && (
            <article>
              {post.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.tags.map((tag) => (
                    <span key={tag} className="bg-[#DB5A14] text-white text-xs font-bold px-3 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              )}
              <h1 className="text-4xl md:text-5xl font-black text-[#004D51] mb-4 leading-tight" data-testid="blog-post-title">
                {post.title}
              </h1>
              <div className="flex items-center gap-5 text-sm text-[#545759] mb-8">
                <span className="flex items-center"><User className="w-4 h-4 mr-1" />{post.author}</span>
                <span className="flex items-center"><Calendar className="w-4 h-4 mr-1" />{formatPostDate(post.published_at || post.created_at)}</span>
              </div>

              {post.cover_image && (
                <div className="aspect-video rounded-2xl overflow-hidden border-4 border-[#004D51] mb-10">
                  <img src={resolveImageUrl(post.cover_image)} alt={post.title} className="w-full h-full object-cover" />
                </div>
              )}

              <div
                className="prose-vesta max-w-none text-lg text-[#2b2b2b] leading-relaxed"
                data-testid="blog-post-body"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body || '') }}
              />
            </article>
          )}
        </div>
      </div>

      <StoreFooter />
    </div>
  );
};

export default BlogPost;
